'use client';

import { usePathname } from 'next/navigation';
import React, { Profiler, useCallback, useContext, useEffect, useMemo } from 'react';

import { isClient, isTestingEnv } from '@/app/_utils/env';
import { useCitiboxData } from '@/hooks/useCitiboxData';
import { AnalyticsEvents } from '@/lib/google/AnalyticsEvents';
import { analyticsCouriers } from '@/lib/google/analytics';

export interface PerformanceType {
  defaultPayload(citiboxData: object): object;
}

export const PerformanceDropOff: PerformanceType = {
  defaultPayload(citiboxData: object): object {
    return {
      feature: 'dropoff',
      feature_uuid: citiboxData['getDropOffId']?.() || ''
    };
  }
};

export interface PerformanceContextType {
  startBenchmark: (type: PerformanceType, payload?: object | undefined) => Promise<void>;
  stopBenchmark: (type: PerformanceType, payload?: object | undefined) => Promise<void>;
}

const PerformanceContext = React.createContext<PerformanceContextType | undefined>(undefined);

export const PerformanceProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const citiboxData = useCitiboxData();
  const pathname = usePathname();
  const [lastPathReported, setLastPathReported] = React.useState('');

  const startBenchmark = useCallback(
    (type: PerformanceType, payload?: object | undefined): Promise<void> => {
      if (isClient() && isTestingEnv()) {
        return Promise.resolve();
      }
      return analyticsCouriers.sendEvent(AnalyticsEvents.START_BENCHMARK, {
        ...payload,
        ...type.defaultPayload(citiboxData),
        page: pathname
      });
    },
    [pathname, citiboxData]
  );

  const stopBenchmark = useCallback(
    (type: PerformanceType, payload?: object | undefined): Promise<void> => {
      if (isClient() && isTestingEnv()) {
        return Promise.resolve();
      }
      return analyticsCouriers.sendEvent(AnalyticsEvents.END_BENCHMARK, {
        ...payload,
        ...type.defaultPayload(citiboxData),
        page: pathname
      });
    },
    [pathname, citiboxData]
  );

  const onRender = useCallback(
    (_id: string, phase: string, actualDuration: number) => {
      if (phase === 'update' && lastPathReported !== pathname) {
        //console.log(`Reporting page rendered to analytics: ${pathname}: ${actualDuration}ms`);
        analyticsCouriers
          .sendEvent(AnalyticsEvents.PAGE_RENDERED, {
            page: pathname,
            actualDuration: actualDuration
          })
          .then();
        setLastPathReported(pathname);
      }
    },
    [pathname, lastPathReported]
  );

  useEffect(() => {
    if (lastPathReported !== pathname) {
      setLastPathReported('');
    }
  }, [pathname, lastPathReported]);

  const value: PerformanceContextType = useMemo(
    () => ({
      startBenchmark: startBenchmark,
      stopBenchmark: stopBenchmark
    }),
    [startBenchmark, stopBenchmark]
  );

  return (
    <PerformanceContext.Provider value={value}>
      <Profiler
        id={'CitiboxProfiler'}
        onRender={onRender}>
        {children}
      </Profiler>
    </PerformanceContext.Provider>
  );
};

export const usePerformance = () => {
  const context = useContext(PerformanceContext);
  if (!context) {
    throw new Error('usePerformance must be a child of PerformanceProvider');
  }
  return context;
};
